<template>
  <div>
    <div class="charttitle" v-if="title">{{ title }}</div>
    <fin-data-loader :resolver="loadData" :data-adapter="adapt" :small-loader="small" overlay ref="loader">
      <template #content="{data}">
        <fin-chart2 :options="data"/>
      </template>
    </fin-data-loader>
  </div>

</template>

<script>
import FinChart2 from "@/components/common/Chart2/FinChart2"
import FinDataLoader from "@/components/common/DataLoader"
import FinChartBuilder from "@/components/common/Chart2/FinChartBuilder"
import {API, graphqlOperation} from "@/plugins/finAmplifyModules"
import colors from 'fincraft-lib-basics/src/js/colors'
import {DEFAULT_CHART_OPTIONS} from "@/components/common/Chart2/HighChartInit"

function createQuery(small, investedCapital, performance, realtime = false) {
  let additional = []
  if (investedCapital) {
    additional.push('i:investedCapital')
  }

  const y = performance ? 'growthAmount' : 'currentValue'
  if (performance) {
    additional.push('curr:currentValue')
  }

  const series = small ? 'timeSeries1M' : realtime ? 'timeSeriesRT' : 'timeSeries'
  const timeSeriesStr = `${realtime ? 'timeSeriesRT' : 'timeSeries'}: ${series}(assetClass:$assetClassId){
                y:${y}
                x:timestamp
                ${additional.join()}
              }`
  return `query assetsChart($portfolioId: ID!, $assetClassId: String!){
          getPortfolio(id: $portfolioId){
            ${timeSeriesStr}
          }
        }
      `
}

export default {
  name: 'fin-portfolio-value-chart',
  components: {
    FinDataLoader,
    FinChart2,
  },
  props: {
    portfolio: {},
    assetClass: {},
    size: {
      type: String,
      default: 'big'
    },
    title: {
      type: String,
    },
    investedCapital: {
      type: Boolean
    },
    performance: {
      type: Boolean,
    }
  },
  data() {
    return {
      selectedRangeModel: 2
    }
  },
  computed: {
    tooltipTitle() {
      return this.assetClass ? this.assetClass.name : this.portfolio.name
    },
    small() {
      return this.size === 'small'
    },
    portfolioStatus() {
      return this.portfolio.status
    }
  },
  watch: {
    portfolioStatus() {
      this.reloadChartData()
    }
  },
  methods: {
    async reloadChartData() {
      console.log("reloadChartData")
      await this.$refs.loader._fetchData()
    },
    async loadData() {
      let result = undefined

      try {
        result = await API.graphql(graphqlOperation(createQuery(this.small, this.investedCapital, this.performance), {
          portfolioId: this.portfolio.id,
          assetClassId: this.assetClass ? this.assetClass.id : 'TOTAL'
        }))
      } catch (e) {
        console.error("failed to load timeSeries data", e)
      }

      try {
        const res = await API.graphql(graphqlOperation(createQuery(this.small, this.investedCapital, this.performance, true), {
          portfolioId: this.portfolio.id,
          assetClassId: this.assetClass ? this.assetClass.id : 'TOTAL'
        }))

        if (result) {
          // put timeSeries and timeSeriesRT in the same serie
          result.data.getPortfolio.timeSeries = result.data.getPortfolio.timeSeries.concat(res.data.getPortfolio.timeSeriesRT)
        } else {
          result = res
        }
      } catch (e) {
        console.error("failed to load timeSeriesRT data", e)
      }

      return result ? result.data : undefined
    },
    async adapt({getPortfolio: data}) {
      console.log("adapt data", data)

      // series based on close values
      let seriesData = FinChartBuilder.mapTimeSeriesPoint(data.timeSeries, 'y', 'x', this.performance)
      // TODO: Backend data are not sorted properly yet. The following line can be removed once the data are sorted properly
      //seriesData = seriesData.sort((a,b) => a[0] - b[0])
      //console.log("seriesData last", seriesData[seriesData.length - 1])

      const color = this.performance ? colors["soft-green"] : null
      const result = FinChartBuilder.createCardSelectorChart(this.performance ? 'Performance' : 'Wertentwicklung: ' + this.tooltipTitle, seriesData, this.size, this.$vuetify.breakpoint, false, '€',
        color, !this.performance, !this.investedCapital, undefined, DEFAULT_CHART_OPTIONS.rangeSelector.buttons.findIndex(r => r.finTimeSeriesSuffix !== "1M"))

      // series investment
      if (this.investedCapital) {
        const investedCapitalData = FinChartBuilder.mapTimeSeriesPoint(data.timeSeries, 'i', 'x')
        result.series.push({
            name: 'Investiert: ' + this.tooltipTitle,
            data: investedCapitalData,
            type: "line",
            tooltip: {
              valueDecimals: 2
            },
            lineWidth: 0.5
          })
      }

      if (this.performance && result.series?.length) {
        result.series[0].negativeColor = colors["pale-red"]

        result.tooltip = {
          pointFormat: '<b>{point.curr:.2f}€</b> ({point.change}€)<br/>',
          changeDecimals: 2,
          valueDecimals: 2
        }
      }

      return result
    }
  }
}
</script>

<style scoped>
.charttitle {
  font-family: "Gilroy";
  font-size: 1.7em;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.1;
  letter-spacing: normal;
}
</style>
